import {
  Avatar,
  AvatarImage,
  Button,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from '@growdash/design-system/atoms';
import type { IBrand, ICommissonGroupByBrand } from '@utils/interfaces';
import React, { useCallback, useState } from 'react';

interface AddCommissionFormProps {
  CHANNELS: IBrand[];
  onSubmit: (
    commission: ICommissonGroupByBrand,
    channelId: number
  ) => Promise<void>;
  onCancel: () => void;
  enqueueSnackbar: (message: string, options: { variant: string }) => void;
}

// Create a local type that allows empty strings for inputs
type CommissionFormState = {
  id: number;
  channel: IBrand;
  yearly_renewal_fee: number | '';
  monthly_subscription_fee: number | '';
  percentage_commission: number | '';
  flat_fee_per_order: number | '';
};

const defaultCommissionState: CommissionFormState = {
  id: 0,
  channel: { id: 0, name: '' },
  yearly_renewal_fee: '',
  monthly_subscription_fee: '',
  percentage_commission: '',
  flat_fee_per_order: ''
};

export const AddCommissionForm: React.FC<AddCommissionFormProps> = React.memo(
  function AddCommissionForm({
    CHANNELS,
    onSubmit,
    onCancel,
    enqueueSnackbar
  }) {
    const [commission, setCommission] = useState<CommissionFormState>(
      defaultCommissionState
    );
    const [selectedChannel, setSelectedChannel] = useState<number | null>(null);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // For percentage commission, validate if not an empty string
        if (name === 'percentage_commission' && value !== '') {
          const numericValue = Number(value);
          if (numericValue < 0 || numericValue > 100) {
            enqueueSnackbar('Commission percentage must be between 0 and 100', {
              variant: 'error'
            });
            return;
          }
        }
        // Store raw value (empty string remains as '')
        setCommission(prev => ({ ...prev, [name]: value }));
      },
      [enqueueSnackbar]
    );

    const handleChannelClick = useCallback((channel: IBrand) => {
      setSelectedChannel(channel.id);
      setCommission(prev => ({ ...prev, channel }));
    }, []);

    const handleSubmit = useCallback(async () => {
      if (!selectedChannel) {
        enqueueSnackbar('Please select a channel', { variant: 'error' });
        return;
      }
      // Convert input strings to numbers
      const parsedCommission: ICommissonGroupByBrand = {
        id: commission.id,
        channel: commission.channel,
        yearly_renewal_fee: Number(commission.yearly_renewal_fee),
        monthly_subscription_fee: Number(commission.monthly_subscription_fee),
        percentage_commission: Number(commission.percentage_commission),
        flat_fee_per_order: Number(commission.flat_fee_per_order)
      };

      // Check if any conversion failed (i.e. empty inputs result in NaN)
      if (
        isNaN(parsedCommission.yearly_renewal_fee) ||
        isNaN(parsedCommission.monthly_subscription_fee) ||
        isNaN(parsedCommission.percentage_commission) ||
        isNaN(parsedCommission.flat_fee_per_order)
      ) {
        enqueueSnackbar('Please fill out all numeric fields', {
          variant: 'error'
        });
        return;
      }

      await onSubmit(parsedCommission, selectedChannel);
    }, [commission, onSubmit, selectedChannel, enqueueSnackbar]);

    return (
      <div className='flex flex-col gap-2'>
        <DialogHeader>
          <DialogTitle>Add Commission</DialogTitle>
        </DialogHeader>
        <div className='mt-5 mb-2 flex items-center'>
          <Label>Channels:</Label>
          {CHANNELS.map((option: IBrand) => (
            <Button
              key={option.id}
              variant='outline'
              onClick={e => {
                e.preventDefault();
                handleChannelClick(option);
              }}
              className={`ml-1 rounded-full w-[30px] h-[30px] p-0 flex-col hover:bg-transparent focus-visible:ring-2 focus-visible:ring-offset-2 ${
                selectedChannel === option.id
                  ? 'border-2 border-[#02D2C5] p-0.5'
                  : ''
              }`}
            >
              <Avatar className='w-[25px] h-[25px]'>
                <AvatarImage
                  src={`/assets/channel_logo/${option.name.toLowerCase()}.png`}
                  alt={option.name}
                />
              </Avatar>
            </Button>
          ))}
        </div>
        <Label>Yearly Renewal Fee</Label>
        <Input
          name='yearly_renewal_fee'
          placeholder='Yearly Renewal Fee'
          type='number'
          value={commission.yearly_renewal_fee}
          onChange={handleInputChange}
          className='mb-2 no-arrows'
        />
        <Label>Monthly Subscription Fee</Label>
        <Input
          name='monthly_subscription_fee'
          placeholder='Monthly Subscription Fee'
          type='number'
          value={commission.monthly_subscription_fee}
          onChange={handleInputChange}
          className='mb-2 no-arrows'
        />
        <Label>Percentage Commission</Label>
        <Input
          name='percentage_commission'
          placeholder='Commission %'
          type='number'
          value={commission.percentage_commission}
          onChange={handleInputChange}
          className='mb-2 no-arrows'
        />
        <Label>Flat Fee Per Order</Label>
        <Input
          name='flat_fee_per_order'
          placeholder='Flat Fee Per Order'
          type='number'
          value={commission.flat_fee_per_order}
          onChange={handleInputChange}
          className='mb-2 no-arrows'
        />
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='default' onClick={handleSubmit}>
            Add
          </Button>
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
);
